import React, { useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useExpanded, useTable } from "react-table";
import { EyeIcon, TrashIcon, PencilIcon } from "@heroicons/react/outline";
import { useDispatch, useSelector } from "react-redux";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";
import { Menu } from "@headlessui/react";

import { getFullName } from "utils";
import { Constants } from "config/constants";
import Pagination from "components/lib/Shared/Pagination";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { CORPORATES } from "components/PLCTABS/SFLdata/config/constants";
import CompanyOfficialSubRow from "components/CompanyDashboard/BusinessDetails/CompanyOfficialSubRow";
import CessationSubRowForDelete from "./CessationSubRow/index";
import TableActionMenuWrapper from "components/lib/Shared/TableActionMenuWrapper";
import Dialog from "components/lib/Shared/Dialog";
import Button from "components/lib/Shared/Button";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import { deleteInitiatedTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import AvatarIcon from "components/lib/Shared/Icons/AvatarIcon";
import { useToast } from "hooks/useToast";
import { deleteCompanyOfficialAsync } from "state/slices/tables/companyOfficialsSlice";

const ManageCessationOfficialsTable = React.memo(
  ({
    companyId,
    data,
    totalCount,
    currentTab,
    currentTransaction,
    isInTransactionEditContext,
    isLoading,
    typeOfTable = "",
    selectType,
    isChangeList,
    selectedSubOption,
    setChangeList,
    setPayload = () => {},
    setTrxId = () => {},
    onRemoveOfficial,
  }) => {
    const dispatch = useDispatch();
    const [initialRows, setInitialRows] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [actionType, setActionType] = useState("");
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [
      showDeleteChangelistOfficialDialog,
      setshowDeleteChangelistOfficialDialog,
    ] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [rowsPerPage] = useState(10);
    const [transactionId, setTransactionId] = useState(
      currentTransaction?._id || null
    );

    // Move this to the top level of your component (not inside if)

    const companyDashboardSlice = useSelector(
      (state) => state.companyDashboardSlice
    );
    const { getCompanyOfficialsStatus } = companyDashboardSlice;

    const columns = useMemo(() => {
      const tempTabs = [
        {
          Header: () => "Full Name",
          id: "Name",
          accessor: "fullName",
          Cell: ({ row: { original } }) => {
            const { names, fileUploads } = original;
            const fullName = getFullName(names) || "N/A";
            if (
              CORPORATES.includes(original.idType) ||
              (original.designation === "Joint Shareholder" &&
                original.isDirector !== "Yes")
            ) {
              return <h1>{fullName}</h1>;
            }
            return (
              <div className="flex items-center space-x-1">
                <div className={`flex-shrink-0`}>
                  {fileUploads?.passportPhoto ? (
                    <img
                      className="w-8 h-8 rounded-full"
                      src={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${fileUploads?.passportPhoto}`}
                      alt={fullName}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full">
                      <div className="relative w-8 h-8 overflow-hidden bg-gray-100 rounded-full dark:bg-gray-600">
                        <AvatarIcon />
                      </div>
                    </div>
                  )}
                </div>
                <div className="px-3">
                  <h1 className="">{fullName}</h1>
                </div>
              </div>
            );
          },
          SubCell: (cellProps) => <>{cellProps.value}</>,
        },
        {
          Header: () => "ID Type",
          id: "ID Type",
          accessor: "idType",
        },
        ...(typeOfTable === "recent-changes"
          ? [
              {
                Header: () => "Trx Ref",
                id: "TRX REF",
                Cell: ({ row }) =>
                  row.original?.brsRef ? row.original?.brsRef : "-",
              },
              {
                Header: () => "Date",
                id: "DATE",
                Cell: ({ row }) =>
                  row.original?.createdAt
                    ? format(new Date(row.original?.createdAt), "yyyy/MM/dd")
                    : "N/A",
              },
            ]
          : []),
        {
          Header: () => "Designation",
          id: "Designation",
          Cell: ({ row: { original } }) => {
            return Array.isArray(original.fullName)
              ? "Joint Shareholder"
              : original.isDirector === "Yes"
              ? "Director"
              : original.designation ?? "";
          },
        },
        {
          Header: () => "Status",
          id: "manageModuleStatus",
          Cell: ({ row: { original } }) =>
            original?.manageModuleStatus ? original?.manageModuleStatus : "-",
        },
        {
          Header: "Action",
          Cell: ({ row }) => {
            return (
              <ActionButtons
                actionType={actionType}
                isChangeList={isChangeList}
                setActionType={setActionType}
                row={row}
                setShowConfirmationDialog={setShowConfirmationDialog}
                setSelectedRow={setSelectedRow}
                setshowDeleteChangelistOfficialDialog={
                  setshowDeleteChangelistOfficialDialog
                }
              />
            );
          },
        },
      ];
      return tempTabs;
    }, [typeOfTable, actionType]);

    const renderRowSubComponent = useCallback(
      ({ row, rowProps, visibleColumns }) => {
        if (!actionType)
          return (
            <tr>
              <td></td>
            </tr>
          );
        if (actionType === "delete") {
          return (
            <CessationSubRowForDelete
              row={row}
              rowProps={rowProps}
              currentTransaction={currentTransaction}
              visibleColumns={visibleColumns}
              currentTab={currentTab}
              isInTransactionEditContext={isInTransactionEditContext}
              initialRows={initialRows}
              selectType={selectType}
              setChangeList={setChangeList}
              setPayload={setPayload}
              setTrxId={(id) => {
                setTransactionId(id);
                setTrxId(id);
              }}
              onRemoveOfficial={onRemoveOfficial}
            />
          );
        }
        if (actionType === "edit") {
          return (
            <CessationSubRowForDelete
              row={row}
              rowProps={rowProps}
              visibleColumns={visibleColumns}
              currentTab={currentTab}
              isInTransactionEditContext={isInTransactionEditContext}
              initialRows={initialRows}
              selectType={selectType}
              setChangeList={setChangeList}
              setPayload={setPayload}
              setTrxId={(id) => {
                setTransactionId(id);
                setTrxId(id);
              }}
              onRemoveOfficial={onRemoveOfficial}
              isEditMode={true}
              currentTransaction={currentTransaction}
            />
          );
        }
        return (
          <CompanyOfficialSubRow
            row={row}
            data={data}
            isChangeList={isChangeList}
            rowProps={rowProps}
            visibleColumns={visibleColumns}
            currentTab={currentTab}
            initialRows={initialRows}
          />
        );
      },
      [
        actionType,
        currentTab,
        initialRows,
        selectType,
        setChangeList,
        setPayload,
        setTrxId,
        data,
        isChangeList,
        isInTransactionEditContext,
        currentTransaction,
        onRemoveOfficial,
      ]
    );

    const tableProps = useMemo(
      () => ({
        columns,
        data,
        totalCount,
        renderRowSubComponent,
        dataRowCellStyle: "whitespace-normal",
        actionType,
        isLoading: getCompanyOfficialsStatus.status === "loading",
        typeOfTable,
        currentPage,
        setCurrentPage,
        paginate: true,
        totalRows: totalCount,
      }),
      [
        columns,
        data,
        totalCount,
        renderRowSubComponent,
        actionType,
        getCompanyOfficialsStatus.status,
        typeOfTable,
        currentPage,
      ]
    );

    return (
      <div className="w-full bg-white shadow">
        <div className="w-full">
          <ManageTable {...tableProps} />
        </div>
        {showConfirmationDialog && (
          <ResignationOrRemovalModal
            row={selectedRow}
            setActionType={setActionType}
            setShowConfirmationDialog={setShowConfirmationDialog}
          />
        )}
        {showDeleteChangelistOfficialDialog && (
          <DeleteChangelistOfficialModal
            row={selectedRow}
            setActionType={setActionType}
            setshowDeleteChangelistOfficialDialog={
              setshowDeleteChangelistOfficialDialog
            }
            currentTransaction={currentTransaction}
            transactionId={transactionId}
            onRemoveOfficial={onRemoveOfficial}
            setTrxId={setTrxId}
          />
        )}
      </div>
    );
  }
);

ManageCessationOfficialsTable.displayName = "ManageCessationOfficialsTable";

const ActionButtons = ({
  actionType,
  setActionType,
  isChangeList,
  row,
  setShowConfirmationDialog,
  setSelectedRow,
  setshowDeleteChangelistOfficialDialog,
}) => {
  const { selectType, reason } = JSON.parse(
    window.localStorage.getItem("initiateTransactionObj") || "{}"
  );

  return (
    <div>
      <TableActionMenuWrapper>
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              onClick={() => {
                row.toggleRowExpanded();
                setActionType("view");
              }}
              className={`${
                active ? "bg-violet-500 text-white" : "text-gray-900"
              } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
            >
              {row.isExpanded ? (
                <>
                  <span className="ml-4">Close</span>
                </>
              ) : (
                <>
                  <EyeIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                  View
                </>
              )}
            </button>
          )}
        </Menu.Item>
        {isChangeList && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => {
                  row.toggleRowExpanded();
                  setActionType("edit");
                  setSelectedRow(row);
                }}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <PencilIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                Edit
              </button>
            )}
          </Menu.Item>
        )}
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              onClick={() => {
                if (isChangeList) {
                  setshowDeleteChangelistOfficialDialog(true);
                  setSelectedRow(row);
                } else {
                  setShowConfirmationDialog(true);
                  setSelectedRow(row);
                  setActionType("delete");
                }
              }}
              className={`${
                active ? "bg-violet-500 text-white" : "text-red-500"
              } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
            >
              <TrashIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              {isChangeList
                ? "Delete"
                : selectType === "resignation"
                ? "Resignation"
                : selectType === "removal"
                ? "Removal"
                : selectType === "operation"
                ? `${reason === "retirement" ? "Retirement" : "Rotation"}`
                : selectType === "lackOfCapacity"
                ? `${
                    reason === "death"
                      ? "Death"
                      : reason === "bankruptcy"
                      ? "Bankruptcy"
                      : reason === "insanity"
                      ? "Mental Incapacity"
                      : "Court Order"
                  }`
                : "Change"}
            </button>
          )}
        </Menu.Item>
      </TableActionMenuWrapper>
    </div>
  );
};

const ResignationOrRemovalModal = ({
  row,
  setActionType,
  setShowConfirmationDialog,
}) => {
  const params = useParams();
  const { changeType, applicationType, selectType } = JSON.parse(
    window.localStorage.getItem("initiateTransactionObj") || "{}"
  );

  const [modelText, setModelText] = useState({
    heading: "Your details will be deleted",
    text: "Are you sure you want to proceed with cessation of the selected official from your list?",
  });
  const handleClose = () => {
    setShowConfirmationDialog(false);
  };

  const existingRequestHandler = (row) => {
    if (selectType && applicationType && changeType) {
      setModelText({
        heading: "Oops!, you have not completed request as initiated earlier",
        text: "Please complete the previous request to proceed further!",
      });
    } else {
      handleCompanyMaintenanceFormData(
        "new-request",
        [
          {
            companyId: params.companyId,
            changeType,
            applicationType,
            sourceCollectionId: row.original._id,
            formData: {
              selectType,
              selectedRow: {
                index: row.index,
                id: row.id,
                isExpanded: true,
              },
              ...row.original,
            },
          },
        ],
        params.companyId
      );
    }
  };
  return (
    <Dialog open={true} onClose={handleClose}>
      <h2 className="text-xl font-medium text-red-500">{modelText.heading}</h2>
      <div className="py-5 whitespace-normal">
        <span className="font-normal">{modelText.text}</span>
      </div>
      <div className="flex justify-end">
        <div className="p-1">
          <Button
            className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md shadow-sm hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed`}
            onClick={() => setShowConfirmationDialog(false)}
          >
            Cancel
          </Button>
        </div>
        <div className="p-1">
          <Button
            className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-sflPrimary hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed`}
            onClick={() => {
              row.toggleRowExpanded();
              existingRequestHandler(row);
              setActionType("delete");
              setShowConfirmationDialog(false);
            }}
          >
            I Confirm
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

const DeleteChangelistOfficialModal = ({
  row,
  setActionType,
  setshowDeleteChangelistOfficialDialog,
  currentTransaction,
  transactionId,
  onRemoveOfficial,
  setTrxId,
}) => {
  const params = useParams();
  const dispatch = useDispatch();
  const { toast } = useToast(5000);
  const [submitting, setSubmitting] = useState(false);

  const [modelText, setModelText] = useState({
    heading: "Confirm Removal",
    text: "Are you sure you want to remove this official and all associated documents from the transaction?",
  });

  const handleClose = () => {
    setshowDeleteChangelistOfficialDialog(false);
  };

  const { transaction } = useMemo(
    () =>
      JSON.parse(window.localStorage.getItem("initiateTransactionObj") || "{}"),
    []
  );

  const handleConfirmDelete = async () => {
    setSubmitting(true);

    // Use transactionId if provided, otherwise fall back to currentTransaction?._id
    const targetTransactionId =
      transactionId || currentTransaction?._id || transaction?._id;
    if (!targetTransactionId) {
      toast("error", "Transaction ID not found");
      setSubmitting(false);
      return;
    }

    try {
      let officialsKey = "officials";
      setTrxId(targetTransactionId);
      if (Array.isArray(currentTransaction?.individualOfficials)) {
        officialsKey = "individualOfficials";
      } else if (Array.isArray(currentTransaction?.corporateOfficials)) {
        officialsKey = "corporateOfficials";
      }

      const updatedOfficials = (
        currentTransaction?.[officialsKey] || []
      ).filter((o) => o._id !== row.original._id);

      if (updatedOfficials.length > 0) {
        // Update transaction if other officials remain

        await handleCompanyMaintenanceFormData(
          "existing-request",
          {
            ...currentTransaction,
            [officialsKey]: updatedOfficials,
            documentId: targetTransactionId,
          },
          currentTransaction?.companyId || params.companyId
        );
        toast("success", "Official removed successfully");
      } else {
        // Delete entire transaction if last official

        if (currentTransaction?.changeType === "APPOINTMENT") {
          if (currentTransaction?.[officialsKey]?.length > 0) {
            for (const official of currentTransaction[officialsKey]) {
              await dispatch(
                deleteCompanyOfficialAsync({
                  officialId: official._id,
                  idType: official.idType,
                  companyId: currentTransaction.companyId,
                })
              );
            }
          }
        }

        await dispatch(
          deleteInitiatedTransactionChangeAsync({
            companyId: currentTransaction?.companyId || params.companyId,
            initiateId: targetTransactionId,
          })
        );
        // Clear transaction from local storage
        localStorage.removeItem("initiateTransactionObj");
        toast("success", "Transaction has been deleted successfully");
      }

      // Update UI state
      onRemoveOfficial(row.original._id);
      setshowDeleteChangelistOfficialDialog(false);
    } catch (error) {
      console.error(
        "DeleteChangelistOfficialModal: Error removing official:",
        error
      );
      toast("error", "Failed to remove official");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onClose={handleClose}>
      <h2 className="text-xl font-medium text-red-500">{modelText.heading}</h2>
      <div className="py-5 whitespace-normal">
        <span className="font-normal">{modelText.text}</span>
      </div>
      <div className="flex justify-end">
        <div className="p-1">
          <Button
            className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md shadow-sm hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed`}
            onClick={() => setshowDeleteChangelistOfficialDialog(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
        <div className="p-1">
          <Button
            className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-sflPrimary hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed`}
            onClick={handleConfirmDelete}
            disabled={submitting}
          >
            {submitting ? (
              <div className="flex">
                <SpinnerIcon />
                Deleting...
              </div>
            ) : (
              "I Confirm"
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

const ManageTable = React.memo(
  ({
    columns,
    data,
    renderRowSubComponent,
    headingStyle,
    headingRowStyle,
    dataRowStyle,
    dataRowCellStyle,
    hideColumnsRow,
    isLoading,
    currentPage,
    rowsPerPage = 10,
    showSnColumn = true,
    totalRows,
    setCurrentPage,
    paginate,
    typeOfTable,
  }) => {
    const { applicationType } = useMemo(
      () =>
        JSON.parse(
          window.localStorage.getItem("initiateTransactionObj") || "{}"
        ),
      []
    );

    const tableColumns = useMemo(() => {
      if (!showSnColumn) {
        return columns;
      }
      return [
        {
          Header: "Sn",
          accessor: "sn",
        },
        ...columns,
      ];
    }, [showSnColumn, columns]);

    const tableData = useMemo(() => {
      return showSnColumn
        ? data?.map((item, index) => {
            return {
              ...item,
              sn:
                item.id === "total"
                  ? ""
                  : currentPage === undefined
                  ? index + 1
                  : index + 1 + (currentPage - 1) * rowsPerPage,
            };
          })
        : data;
    }, [showSnColumn, data, currentPage, rowsPerPage]);

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      toggleAllRowsExpanded,
      visibleColumns,
    } = useTable(
      {
        columns: tableColumns,
        data: tableData,
        autoResetExpanded: false,
      },
      useExpanded
    );

    const emptyStateMessage = useMemo(
      () => (
        <tr>
          <td className="text-center py-4" colSpan={columns.length + 1}>
            No Other {applicationType || "Officials"} to Cease.
          </td>
        </tr>
      ),
      [applicationType, columns.length]
    );

    return (
      <div className="">
        <div className="w-full overflow-auto">
          <table className="min-w-full min-h-full" {...getTableProps()}>
            {!hideColumnsRow && (
              <thead className={"bg-[#31415d] align-top"}>
                {headerGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    className={headingRowStyle}
                  >
                    {headerGroup.headers.map((column) => (
                      <th
                        scope="col"
                        className={twMerge(
                          "px-6 py-3 text-left text-sm font-medium tracking-wider text-slate-50",
                          headingStyle,
                          column.headingStyle
                        )}
                        {...column.getHeaderProps()}
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
            )}
            <tbody
              className="align-top bg-white relative"
              {...getTableBodyProps()}
            >
              {data.length === 0 &&
                (typeOfTable === "recent-changes" ||
                  typeOfTable === "cessation") &&
                emptyStateMessage}
              {rows.map((row, i) => {
                row["toggleAllRowsExpanded"] = toggleAllRowsExpanded;
                prepareRow(row);
                const rowProps = row.getRowProps();
                return (
                  <React.Fragment key={rowProps.key}>
                    <tr
                      {...rowProps}
                      className={twMerge(
                        dataRowStyle,
                        data.length === row.index + 1 ? "border-b-0" : ""
                      )}
                    >
                      {row.cells.map((cell) => {
                        return (
                          <td
                            className={twMerge(
                              "px-6 py-4 text-sm text-primary",
                              dataRowCellStyle,
                              cell.column.className
                            )}
                            {...cell.getCellProps()}
                          >
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                    {row.isExpanded &&
                      renderRowSubComponent({
                        row,
                        rowProps,
                        visibleColumns,
                      })}
                  </React.Fragment>
                );
              })}
              {isLoading && (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-gray-200/50 z-10"
                  id="loading-overlay"
                >
                  <SpinnerIcon textColor="text-gray-400" />
                </div>
              )}
            </tbody>
          </table>
        </div>
        {paginate && totalRows > Constants.PAGINATION_LIMIT && (
          <Pagination
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            totalRows={totalRows}
            setCurrentPage={setCurrentPage}
            className="mt-0"
            isLoading={isLoading}
          />
        )}
      </div>
    );
  }
);

ManageTable.displayName = "ManageTable";

export default ManageCessationOfficialsTable;
